import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Clock, CalendarClock, Coins, CircleDollarSign, Loader2, RefreshCw } from "lucide-react";

export default function MarketUpdateControls({ lang = "it", lastUpdated, source, ageHours, refreshTick = 0 }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/market/apify-usage`); setUsage(data); }
    catch { setUsage(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshTick]);

  const fmtDT = (iso) => { try { const dt = new Date(iso); return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`; } catch { return "—"; } };
  const today = usage?.today;
  const monthly = usage?.monthly;
  const next = usage?.next_update;

  // Percentuale di consumo del tetto mensile Apify (per colorare l'avviso).
  const monthPct = (monthly?.available && monthly.used_usd != null && monthly.limit_usd)
    ? Math.round((monthly.used_usd / monthly.limit_usd) * 100) : null;
  const monthColor = monthPct == null
    ? "bg-muted text-muted-foreground"
    : monthPct >= 95 ? "bg-red-500/15 text-red-600 dark:text-red-400"
    : monthPct >= 80 ? "bg-amber-500/15 text-amber-600 dark:text-amber-400"
    : "bg-emerald-500/12 text-emerald-700 dark:text-emerald-400";

  return (
    <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="market-update-controls">
      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="muc-last-updated" title={L("Fonte", "Source") + ": " + (source || "—")}>
        <Clock className="w-3.5 h-3.5" />{L("Ultimo agg.", "Last update")}: {lastUpdated ? fmtDT(lastUpdated) : "—"}{ageHours != null ? ` (${Math.round(ageHours)}h)` : ""}
      </span>
      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="muc-next-update">
        <CalendarClock className="w-3.5 h-3.5" />{L("Prossimo agg.", "Next update")}: {next ? `${fmtDT(next.nightly_grid)} (${next.frequency === "weekly" ? L("settimanale", "weekly") : L("giornaliero", "daily")})` : "—"}
      </span>
      {/* OGGI — solo l'attività e la stima di spesa di oggi (nessun dato mensile). */}
      <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium" data-testid="muc-today"
            title={L("Scansioni Apify effettuate oggi e stima di costo (i 'prezzi raccolti' sono i singoli prezzi competitor, non chiamate)",
                     "Apify scans run today and estimated cost ('prices collected' are individual competitor prices, not calls)")}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Coins className="w-3.5 h-3.5" />}
        {L("Oggi", "Today")}: {today ? `${today.calls} ${L("scansioni", "scans")} · ${today.items} ${L("prezzi raccolti", "prices collected")}${today.est_cost_usd ? ` · ~$${Number(today.est_cost_usd).toFixed(2)}` : ""}` : "—"}
      </span>
      {/* MESE — spesa reale Apify del ciclo mensile con soglia colorata. */}
      {monthly?.available && monthly.used_usd != null && (
        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${monthColor}`} data-testid="muc-month"
              title={L(`Spesa Apify del mese corrente${monthly.limit_usd ? ` sul tetto piano di $${monthly.limit_usd}` : ""}`,
                       `Current-month Apify spend${monthly.limit_usd ? ` against the $${monthly.limit_usd} plan cap` : ""}`)}>
          <CircleDollarSign className="w-3.5 h-3.5" />
          {L("Mese", "Month")}: ${monthly.used_usd}{monthly.limit_usd ? ` / $${monthly.limit_usd}` : ""}{monthPct != null ? ` (${monthPct}%)` : ""}
        </span>
      )}
    </div>
  );
}
